import React from 'react';
import { App } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons';
import {
  MessageGlobalStyle,
  MessageContainer,
  MessageIcon,
  MessageText,
  type MessageType,
} from './styles';

// 重新导出类型以保持向后兼容
export type { MessageType };

// 消息配置接口
interface MessageConfig {
  content: string;
  duration?: number;
  onClose?: () => void;
}

// 自定义消息组件
const CustomMessage: React.FC<{ type: MessageType; content: string }> = ({ type, content }) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleFilled />;
      case 'error':
        return <CloseCircleFilled />;
      case 'warning':
        return <ExclamationCircleFilled />;
      case 'info':
        return <InfoCircleFilled />;
      default:
        return <InfoCircleFilled />;
    }
  };

  return (
    <MessageContainer $type={type}>
      <MessageIcon $type={type}>{getIcon()}</MessageIcon>
      <MessageText>{content}</MessageText>
    </MessageContainer>
  );
};

// 创建一个使用 App 上下文的 Message 管理器
const createMessageManager = () => {
  let messageApi: any = null;
  let isInitialized = false; // 添加初始化状态标记

  // 设置 message API 实例
  const setMessageApi = (api: any) => {
    if (!isInitialized || messageApi !== api) {
      messageApi = api;
      isInitialized = true;
      console.log('Message API initialized:', !!api);
    }
  };

  // 检查API是否可用，如果不可用则使用备用方案
  const ensureApiAvailable = (fallbackContent: string) => {
    if (!messageApi) {
      console.warn('Message API not available, using fallback notification');
      // 使用更友好的备用方案
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        padding: 12px 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 9999;
        font-size: 14px;
        max-width: 400px;
      `;
      notification.textContent = fallbackContent;
      document.body.appendChild(notification);

      // 3秒后自动移除
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      return false;
    }
    return true;
  };

  // 标准化配置
  const normalizeConfig = (config: string | MessageConfig): MessageConfig => {
    if (typeof config === 'string') {
      return { content: config };
    }
    return config;
  };

  // 获取通用样式配置
  const getCommonStyle = () => ({
    marginTop: '0px',
    background: 'transparent',
    boxShadow: 'none',
    padding: '0',
    border: 'none',
    borderRadius: '0',
  });

  return {
    setMessageApi,

    // 成功消息
    success(config: string | MessageConfig) {
      const { content, duration = 3, onClose } = normalizeConfig(config);

      if (!ensureApiAvailable(`✅ ${content}`)) {
        return;
      }

      return messageApi.success({
        content: <CustomMessage type="success" content={content} />,
        duration,
        onClose,
        style: getCommonStyle(),
        className: 'custom-message-container',
        icon: <></>,
      });
    },

    // 错误消息
    error(config: string | MessageConfig) {
      const { content, duration = 3, onClose } = normalizeConfig(config);

      if (!ensureApiAvailable(`❌ ${content}`)) {
        return;
      }

      return messageApi.error({
        content: <CustomMessage type="error" content={content} />,
        duration,
        onClose,
        style: getCommonStyle(),
        className: 'custom-message-container',
        icon: <></>,
      });
    },

    // 警告消息
    warning(config: string | MessageConfig) {
      const { content, duration = 3, onClose } = normalizeConfig(config);

      if (!ensureApiAvailable(`⚠️ ${content}`)) {
        return;
      }

      return messageApi.warning({
        content: <CustomMessage type="warning" content={content} />,
        duration,
        onClose,
        style: getCommonStyle(),
        className: 'custom-message-container',
        icon: <></>,
      });
    },

    // 信息消息
    info(config: string | MessageConfig) {
      const { content, duration = 3, onClose } = normalizeConfig(config);

      if (!ensureApiAvailable(`ℹ️ ${content}`)) {
        return;
      }

      return messageApi.info({
        content: <CustomMessage type="info" content={content} />,
        duration,
        onClose,
        style: getCommonStyle(),
        className: 'custom-message-container',
        icon: <></>,
      });
    },

    // 销毁所有消息
    destroy() {
      if (messageApi) {
        messageApi.destroy();
      }
    },

    // 配置全局参数
    config(options: { top?: number; duration?: number; maxCount?: number; rtl?: boolean }) {
      if (messageApi) {
        messageApi.config(options);
      }
    },
  };
};

// 创建并导出 message 实例
export const message = createMessageManager();

// MessageProvider 组件，用于初始化 message API
export const MessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { message: messageApi } = App.useApp();

  React.useEffect(() => {
    // 初始化 message API
    message.setMessageApi(messageApi);
  }, [messageApi]);

  return (
    <>
      <MessageGlobalStyle />
      {children}
    </>
  );
};

// 默认导出
export default message;
